import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Loans", to: "/loans" },
  { label: "Credit Cards", to: "/credit-cards" },
  { label: "Contact Us", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-[#E4E4E7]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="mx-auto flex h-16 sm:h-20 max-w-7xl items-center justify-between px-6 sm:px-10">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl brand-gradient text-white font-bold">
            C
          </span>
          <span className="text-xl font-bold tracking-tight text-[#09090B]">CARDED</span>
        </Link>

        <div className="hidden items-center gap-9 md:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors hover:text-[#6C2BD9] ${
                  isActive ? "text-[#6C2BD9]" : "text-zinc-600"
                }`
              }
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center gap-2 rounded-full brand-gradient px-6 py-2.5 text-sm font-medium text-white transition-transform hover:scale-105 hover:shadow-[0_8px_24px_rgba(108,43,217,0.3)]"
            data-testid="nav-apply-now"
          >
            Apply Now <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#E4E4E7] text-[#09090B] md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-[#E4E4E7] bg-white md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `border-b border-[#F4F4F5] py-3.5 text-base font-medium ${
                      isActive ? "text-[#6C2BD9]" : "text-zinc-700"
                    }`
                  }
                  data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full brand-gradient px-6 py-3 text-sm font-medium text-white"
                data-testid="nav-mobile-apply"
              >
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
